import { useState } from 'react';
import { produksjonAPI, TalentBehovItem } from '@/services/api';

interface UseTalentBehovCRUDProps {
  produksjonId: number;
  onSuccess?: () => Promise<void>;
}

/**
 * Custom hook for CRUD operations on talent-behov
 * 
 * Ekstrahert fra TalentManagerConcept.tsx for bedre separasjon
 * av business logic fra presentasjon.
 */
export const useTalentBehovCRUD = ({ produksjonId, onSuccess }: UseTalentBehovCRUDProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Juster antall for et talent-behov (+1 eller -1)
   */
  const adjustCount = async (behov: TalentBehovItem, delta: number) => {
    const next = Math.max(0, behov.antall + delta);
    
    // Optimistisk oppdatering
    const original = behov.antall;
    behov.antall = next;
    
    try {
      setLoading(true);
      setError(null);
      await produksjonAPI.updateTalentBehov(produksjonId, behov.id, { antall: next });
      if (onSuccess) await onSuccess();
    } catch (err) {
      // Rollback ved feil
      behov.antall = original;
      setError('Kunne ikke oppdatere antall');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Legg til nytt talent-behov
   */
  const addTalentBehov = async (
    talentNavn: string,
    talentKategori: string,
    antall: number
  ) => {
    try {
      setLoading(true);
      setError(null);
      await produksjonAPI.addTalentBehov(produksjonId, {
        talentNavn,
        talentKategori,
        antall: Math.max(1, antall),
      });
      if (onSuccess) await onSuccess();
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError('Dette talentet er allerede lagt til');
      } else {
        setError('Kunne ikke legge til talent-behov');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fjern talent-behov
   */
  const removeTalentBehov = async (behovId: number) => {
    try {
      setLoading(true);
      setError(null);
      // 404: raden er alt borte, for eksempel slettet av en annen admin. Resultatet er det samme som etter sletting.
      await produksjonAPI.removeTalentBehov(produksjonId, behovId).catch((err: any) => {
        if (err.response?.status !== 404) throw err;
      });
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError('Kunne ikke fjerne talent-behov');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    adjustCount,
    addTalentBehov,
    removeTalentBehov,
    loading,
    error,
    clearError: () => setError(null),
  };
};

